import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Search, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getAllPosts, getCategories, catSlug } from "@/content/blog";
import Seo from "@/lib/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Blog() {
  const posts = getAllPosts();
  const catalog = useMemo(() => getCategories(), []);
  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const [active, setActive] = useState("All");
  const [query, setQuery] = useState("");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const searchable = active === "All" ? posts : posts.filter((p) => p.category === active);
  const q = query.trim().toLowerCase();
  const filtered = q
    ? searchable.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
      )
    : searchable;
  const [featured, ...rest] = filtered;

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await axios.post(`${BACKEND}/api/newsletter`, { email, source: "blog" });
      toast.success("You're on the list — next field note lands in your inbox.");
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe right now. Try again in a moment.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div>
      <Seo
        path="/blog"
        title="The Steady Path Journal — Field Notes on Business Funding"
        description="Plain-English guides to SBA loans, fintech lending, MCA, working capital, and business credit — written by working brokers."
      />

      {/* Editorial hero */}
      <section className="pt-40 pb-20 border-b border-black/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="overline">Vol. I · The journal</div>
          <h1 className="mt-8 font-serif text-6xl md:text-8xl leading-[0.9] tracking-tight max-w-5xl">
            The Steady Path <em className="italic text-[color:var(--gold)]">Journal</em>
          </h1>
          <div className="mt-10 h-px w-12 bg-[color:var(--gold)]" />
          <p className="mt-10 max-w-2xl text-[color:var(--bone)]/75 leading-relaxed text-lg">
            Field notes on business funding — from the brokers who negotiate the deals. No jargon, no theory, no fluff. Just the mechanics of borrowing money well.
          </p>

          {/* Search bar */}
          <div className="mt-14 max-w-2xl">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--bone)]/45 pointer-events-none"
              />
              <input
                data-testid="blog-search-input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search field notes — title, topic, keyword…"
                className="w-full bg-[color:var(--bg-elev)] border border-black/15 pl-11 pr-11 py-3.5 text-[color:var(--bone)] placeholder:text-[color:var(--bone)]/40 focus:outline-none focus:border-[color:var(--gold)] transition-colors font-mono text-sm"
              />
              {query && (
                <button
                  onClick={() => setQuery("")}
                  data-testid="blog-search-clear"
                  aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[color:var(--bone)]/55 hover:text-[color:var(--gold)] transition-colors"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            {query && (
              <div
                data-testid="blog-search-result-count"
                className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/55"
              >
                {filtered.length} match{filtered.length === 1 ? "" : "es"} for "{query}"
              </div>
            )}
          </div>

          {/* Category pills */}
          <div className="mt-10 flex flex-wrap gap-2">
            {categories.map((cat) => {
              const isActive = active === cat;
              return (
                <button
                  key={cat}
                  data-testid={`blog-filter-${cat.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => setActive(cat)}
                  className={`px-4 py-2 font-mono text-[11px] tracking-[0.22em] uppercase rounded-full border transition-all duration-300 ${
                    isActive
                      ? "bg-[color:var(--gold)] text-black border-[color:var(--gold)]"
                      : "border-[color:var(--bone)]/25 text-[color:var(--bone)]/70 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)]"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          {active !== "All" && (
            <div className="mt-6">
              <Link
                to={`/blog/category/${catSlug(active)}`}
                data-testid="blog-view-category-page"
                className="inline-flex items-center gap-2 gold-link font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--gold)]"
              >
                View the {active} landing page <ArrowRight size={12} />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Featured card */}
      {featured && (
        <section className="py-16 md:py-24 border-b border-black/10">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="overline mb-8">Featured field note</div>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid="blog-featured-card"
              className="group grid grid-cols-12 gap-8 items-center"
            >
              <div className="col-span-12 md:col-span-7">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={featured.image}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/15" />
                  <div className="absolute top-6 left-6 font-mono text-[11px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                    {featured.category}
                  </div>
                </div>
              </div>
              <div className="col-span-12 md:col-span-5">
                <div className="inline-flex items-center px-3 py-1 border border-[color:var(--bone)]/20 rounded-full font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/70">
                  {featured.readTime}
                </div>
                <h2 className="mt-6 font-serif text-4xl md:text-5xl leading-[1.05] group-hover:text-[color:var(--gold)] transition-colors">
                  {featured.title}
                </h2>
                <p className="mt-6 text-[color:var(--bone)]/75 leading-relaxed">
                  {featured.excerpt}
                </p>
                <span className="mt-8 inline-flex items-center gap-2 font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
                  Read the field note <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Article grid */}
      {rest.length > 0 && (
        <section className="py-16 md:py-24">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex items-baseline justify-between mb-10">
              <div className="overline">The archive</div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/45">
                {rest.length} article{rest.length === 1 ? "" : "s"}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-8">
              {rest.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, delay: (i % 6) * 0.05 }}
                  className="col-span-12 md:col-span-6 lg:col-span-4 border-t border-black/10 pt-8"
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden mb-6">
                      <img
                        src={p.image}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                      />
                      <div className="absolute inset-0 bg-black/15" />
                      <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--gold)]">
                        {p.category}
                      </div>
                    </div>
                    <div className="inline-flex items-center px-2.5 py-1 border border-[color:var(--bone)]/20 rounded-full font-mono text-[9px] tracking-[0.22em] uppercase text-[color:var(--bone)]/60">
                      {p.readTime}
                    </div>
                    <h3 className="mt-4 font-serif text-2xl md:text-3xl leading-tight group-hover:text-[color:var(--gold)] transition-colors">
                      {p.title}
                    </h3>
                    <p className="mt-4 text-[color:var(--bone)]/70 text-sm leading-relaxed">
                      {p.excerpt}
                    </p>
                  </Link>
                </motion.article>
              ))}
            </div>
          </div>
        </section>
      )}

      {filtered.length === 0 && (
        <section className="py-32 text-center">
          <div className="overline">
            {query ? `No matches for "${query}"` : "No articles in this category yet"}
          </div>
          <button
            onClick={() => {
              setActive("All");
              setQuery("");
            }}
            className="mt-6 gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]"
          >
            Clear filters →
          </button>
        </section>
      )}

      {/* Category directory — SEO landing pages */}
      <section className="py-20 md:py-24 border-t border-black/10 bg-[color:var(--surface)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline">Explore by category</div>
              <h2 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
                Dedicated landing pages, one per topic.
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            {catalog.map((c) => (
              <Link
                key={c.slug}
                to={`/blog/category/${c.slug}`}
                data-testid={`category-directory-${c.slug}`}
                className="col-span-12 sm:col-span-6 lg:col-span-3 group border border-black/10 bg-[color:var(--bg-elev)] p-6 hover:border-[color:var(--gold)] transition-colors"
              >
                <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                  {c.count} article{c.count === 1 ? "" : "s"}
                </div>
                <div className="mt-3 font-serif text-2xl group-hover:text-[color:var(--gold)] transition-colors flex items-center gap-2">
                  {c.name}
                  <ArrowRight
                    size={16}
                    className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all"
                  />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/blog/archive"
              data-testid="blog-archive-link"
              className="inline-flex items-center gap-2 gold-link font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--gold)]"
            >
              Or view the full archive — every article on one page →
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter capture */}
      <section
        className="py-24 md:py-32 border-t border-black/10"
        style={{ background: "linear-gradient(180deg, rgba(196,164,124,0.06), transparent 90%)" }}
      >
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 text-center">
          <div className="inline-flex items-center gap-2 overline">
            <Mail size={12} /> The next field note
          </div>
          <h2 className="mt-6 font-serif text-4xl md:text-6xl leading-[1] tracking-tight">
            Get the next article in <em className="italic text-[color:var(--gold)]">your inbox</em>.
          </h2>
          <p className="mt-6 max-w-xl mx-auto text-[color:var(--bone)]/70 leading-relaxed">
            One article every few weeks. Never spam. Unsubscribe with a single click.
          </p>
          <form
            onSubmit={handleSubscribe}
            data-testid="newsletter-form"
            className="mt-10 flex flex-col sm:flex-row items-stretch gap-3 max-w-lg mx-auto"
          >
            <input
              data-testid="newsletter-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@business.com"
              className="flex-1 bg-[color:var(--bg-elev)] border border-black/15 px-5 py-4 text-[color:var(--bone)] placeholder:text-[color:var(--bone)]/40 focus:outline-none focus:border-[color:var(--gold)] transition-colors"
            />
            <button
              type="submit"
              disabled={subscribing}
              data-testid="newsletter-submit"
              className="bg-[color:var(--gold)] text-black px-8 py-4 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors disabled:opacity-60"
            >
              {subscribing ? "Subscribing…" : "Subscribe"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
